import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDateTime, istanbulDayString, RETURNABLE_JUG_KINDS, t } from '@aquabox/shared';
import type { DepotDayResponse, DepotDayVehicle, MessageKey } from '@aquabox/shared';
import { useApprovalCount, useProducts, useReloadRequests } from '../api/queries';
import {
  useCountWindow,
  useCreateDepotCount,
  useCreateReload,
  useCreateTripLoad,
  useDepotDay,
  useReceiveShipment,
  type DepotCountLine,
  type ReloadBody,
  type TripLoadBody,
} from '../api/depot';
import { ApiError } from '../api/client';
import { Kpi } from '../components/Kpi';
import type { Product } from '../api/types';
import { ReloadQueue } from './Trips';

/**
 * Depo günü (`/depo`, ADR 2026-10-21-depo-gunu). Dört adım: akşam dolum, sabah dağıtım, ara
 * yükleme, sevkiyat. Ekrandaki HİÇBİR adet, fark ya da eşik panelde hesaplanmaz; her rakam
 * `GET /api/stock/depot-day` yanıtından ya da kaydetme uçlarının yanıtından gelir (kural 1, 4).
 */

const STEP_KEYS = [
  'panel.depot.step1',
  'panel.depot.step2',
  'panel.depot.step3',
  'panel.depot.step4',
] as const;

const FOREIGN_MAX = 10;

export function Depot() {
  const [params, setParams] = useSearchParams();
  const step = parseStep(params.get('adim'));
  const depotDay = useDepotDay(istanbulDayString(new Date()));
  const data = depotDay.data;

  if (depotDay.isPending) {
    return <p style={{ padding: 32, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>;
  }
  if (!data) {
    return (
      <div style={{ padding: 32 }}>
        <p style={{ marginBottom: 12 }}>{t('panel.loadFailed')}</p>
        <button className="btn btn-secondary" onClick={() => void depotDay.refetch()}>
          {t('panel.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <p className="muted-line" data-testid="depot-intro">
        {t('depot.intro')}
      </p>
      <TodayCard data={data} />
      <DaySummary data={data} />

      <nav
        aria-label={t('panel.depot.stepsLabel')}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
      >
        {STEP_KEYS.map((key, i) => {
          const n = i + 1;
          const active = n === step;
          return (
            <button
              key={key}
              type="button"
              className={active ? 'btn btn-primary' : 'btn'}
              aria-current={active ? 'step' : undefined}
              onClick={() => setParams({ adim: String(n) })}
            >
              {n}. {t(key)}
            </button>
          );
        })}
      </nav>

      <p className="muted-line" data-testid="depot-step-hint">
        {t(`depot.stepHint${step}` as MessageKey)}
      </p>

      {step === 1 ? <EveningStep data={data} /> : null}
      {step === 2 ? (
        <MorningStep
          data={data}
          onReload={(vehicleId) => setParams({ adim: '3', arac: vehicleId })}
        />
      ) : null}
      {step === 3 ? <ReloadStep data={data} preselectedVehicleId={params.get('arac')} /> : null}
      {step === 4 ? <ShipmentStep /> : null}
    </div>
  );
}

function parseStep(raw: string | null): number {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= STEP_KEYS.length ? n : 1;
}

/**
 * "Bugün" kartı: dört SAYI. Teslim, eksik boş ve açık fark sunucudan gelir (gün İstanbul
 * gününe göre, kural 8); bekleyen onay sayısı ayrı uçtan gelir. Panel HİÇBİR adet hesaplamaz.
 */
function TodayCard({ data }: { data: DepotDayResponse }) {
  const approvals = useApprovalCount();
  const pending = approvals.data?.total ?? 0;
  return (
    <section
      className="card"
      style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <strong style={{ fontSize: 15 }}>{t('panel.depot.todayTitle')}</strong>
      <div className="kpi-grid" style={{ '--kpi-cols': 4, '--kpi-gap': '12px' } as CSSProperties}>
        <Kpi label={t('panel.depot.todayDelivered')} value={data.today.delivered} />
        <Kpi label={t('panel.depot.todayShortfall')} value={data.today.shortfalls} />
        <Kpi label={t('panel.depot.todayOpenVariance')} value={data.today.openVariance} />
        <Kpi label={t('panel.depot.todayPendingApprovals')} value={pending} />
      </div>
    </section>
  );
}

/** Gün özeti; her adımda görünür. Depo null ise (kör sayım) depo rakamları gizlenir. */
function DaySummary({ data }: { data: DepotDayResponse }) {
  const depot = data.depot;
  const hidden = t('panel.depot.hidden');
  return (
    <section
      className="card"
      style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 15 }}>{t('panel.depot.summaryTitle')}</strong>
        {data.blindDay ? <span className="badge-amber">{t('panel.depot.blindDay')}</span> : null}
        {data.reorder.warn ? (
          <span className="badge-amber">{t('panel.depot.reorderWarn')}</span>
        ) : null}
      </div>
      {data.blindDay ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('panel.depot.blindDayHint')}
        </p>
      ) : null}
      {data.reorder.warn ? (
        <p style={{ fontSize: 13, color: 'var(--warning)', fontWeight: 600 }}>
          {t('panel.depot.reorderDetail', {
            avg: data.reorder.avgDailyOut,
            threshold: data.reorder.threshold,
          })}
        </p>
      ) : null}

      <div className="kpi-grid" style={{ '--kpi-cols': 3, '--kpi-gap': '12px' } as CSSProperties}>
        <Kpi label={t('panel.depot.depotFull')} value={depot ? depot.fullJugs : hidden} />
        <Kpi label={t('panel.depot.depotEmpty')} value={depot ? depot.emptyJugs : hidden} />
        <Kpi label={t('panel.depot.depotCases')} value={depot ? depot.crates : hidden} />
        <Kpi label={t('panel.depot.customerDeposit')} value={data.customerDepositJugs} />
        <Kpi label={t('panel.depot.foreignOnShelf')} value={data.foreignEmptyOnShelf} />
        <Kpi label={t('panel.depot.emptyToFactoryToday')} value={data.emptyToFactoryToday} />
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        {data.lastCount
          ? t('panel.depot.lastCount', {
              at: formatDateTime(data.lastCount.countedAt),
              diff: data.lastCount.diffAbs,
            })
          : t('panel.depot.noCount')}
      </p>
    </section>
  );
}

/** Adım 1: araç kartları ve akşam sayımı formu. */
function EveningStep({ data }: { data: DepotDayResponse }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <section
        className="card"
        style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {data.vehicles.map((v) => (
          <VehicleCard key={v.vehicleId} vehicle={v} />
        ))}
      </section>

      <div>
        <button className="btn btn-primary" disabled={open} onClick={() => setOpen(true)}>
          {t('panel.depot.eveningCount')}
        </button>
      </div>
      {open ? <CountForm onDone={() => setOpen(false)} /> : null}
    </>
  );
}

/**
 * Araç kartı (adım 1): plaka, kapasite ve araç stok defteri. Sefer yüklenebiliyorsa
 * (yola çıkmamış sefer ya da yolda olan sefer) altında dolum formu açılır.
 */
function VehicleCard({ vehicle: v }: { vehicle: DepotDayVehicle }) {
  const loadable = v.loadableTripId !== null;
  const reloadable = v.reloadableTripId !== null;
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
        paddingTop: 10,
        borderTop: '1px solid var(--border)',
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 13.5, width: 110 }}>{v.plate}</span>
      <span style={{ flex: 1, fontSize: 13, color: 'var(--text-secondary)' }}>
        {v.capacityJugs !== null
          ? t('panel.depot.capacity', { n: v.capacityJugs })
          : t('panel.depot.noCapacity')}
        {' · '}
        {t('panel.depot.vehicleStock', {
          full: v.stock.fullJugs,
          empty: v.stock.emptyJugs,
          crates: v.stock.crates,
        })}
      </span>
      {v.freeSpace !== null ? (
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: v.freeSpace < 0 ? 'var(--warning)' : undefined,
          }}
        >
          {t('panel.depot.freeSpace', { n: v.freeSpace })}
        </span>
      ) : null}
      {loadable || reloadable ? (
        <div style={{ flexBasis: '100%' }}>
          <LoadPlanCard vehicle={v} />
        </div>
      ) : null}
    </div>
  );
}

/** `planScope` değerinin ekran etiketi (sunucu kümeleri; panelde hesap yok). */
const PLAN_SCOPE_LABEL: Record<DepotDayVehicle['planScope'], string> = {
  OPEN_ALL_DAYS: t('panel.depot.loadScopeAllDays'),
};

/** Son gönderimin gövdesi: yeniden denemede AYNI gövde ve AYNI clientId kullanılır. */
type LoadAttempt =
  | { kind: 'load'; tripId: string; body: TripLoadBody }
  | { kind: 'reload'; body: ReloadBody };

/** Ağ hatası ya da 5xx belirsizdir (istek yazılmış olabilir) → aynı gövdeyle yeniden denenir. */
function isAmbiguous(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500;
  return true;
}

/**
 * Akşam dolum formu: adetler SUNUCUNUN önerisiyle (loadPlan) gelir, patron düzeltebilir.
 * Yola çıkmamış seferde `POST /api/trips/:id/load`, yolda olan seferde
 * `POST /api/reload-requests` (onaylı ara yükleme) yazılır. Onay diyaloğu yoktur.
 */
function LoadPlanCard({ vehicle: v }: { vehicle: DepotDayVehicle }) {
  const loadTripId = v.loadableTripId;
  const reloadTripId = v.reloadableTripId;
  const tripLoad = useCreateTripLoad();
  const reload = useCreateReload();
  const [clientId, setClientId] = useState(() => crypto.randomUUID());
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const line of v.loadPlan) initial[line.productId] = String(line.qty);
    return initial;
  });
  const [saved, setSaved] = useState(false);
  const [attempt, setAttempt] = useState<LoadAttempt | null>(null);

  // Sunucunun önerisi değişirse form yeni adetlerle doldurulur ve gönderim kimliği yenilenir
  // (aynı clientId ile farklı gövde sunucuda eski sonucu oynatır).
  const planKey = v.loadPlan.map((line) => `${line.productId}:${line.qty}`).join(',');
  const seenPlanKey = useRef(planKey);
  useEffect(() => {
    if (seenPlanKey.current === planKey) return;
    seenPlanKey.current = planKey;
    const initial: Record<string, string> = {};
    for (const line of v.loadPlan) initial[line.productId] = String(line.qty);
    setValues(initial);
    setClientId(crypto.randomUUID());
    setAttempt(null);
  }, [planKey, v.loadPlan]);

  const parsed = v.loadPlan.map((line) => ({
    productId: line.productId,
    qty: parseQty(values[line.productId] ?? ''),
  }));
  const invalid = parsed.some((p) => p.qty === null);
  const lines = parsed.flatMap((p) =>
    p.qty !== null && p.qty > 0 ? [{ productId: p.productId, qty: p.qty }] : [],
  );
  const busy = tripLoad.isPending || reload.isPending;
  const canSave = lines.length > 0 && !invalid && !busy;
  const error = tripLoad.error ?? reload.error;

  /**
   * Adet değişti: gönderim kimliği yenilenir ve bekleyen deneme sıfırlanır.
   * Aynı clientId ile farklı gövde gönderilirse sunucu gövdeyi karşılaştırmadan eski sonucu
   * döner (trips.service.ts) — bu yüzden kimlik adetle birlikte değişir.
   */
  function setQty(productId: string, raw: string): void {
    setValues((prev) => ({ ...prev, [productId]: raw }));
    setClientId(crypto.randomUUID());
    setAttempt(null);
    if (tripLoad.isError) tripLoad.reset();
    if (reload.isError) reload.reset();
  }

  /** Başarı: kayıt yapıldı, bildirim gösterilir; form KİLİTLENMEZ, sonraki kayıt yeni clientId ile gider. */
  function onSaved(): void {
    setSaved(true);
    setAttempt(null);
    setClientId(crypto.randomUUID());
  }

  function save(): void {
    if (!canSave) return;
    if (loadTripId !== null) {
      const body: TripLoadBody = { lines, clientId };
      setAttempt({ kind: 'load', tripId: loadTripId, body });
      tripLoad.mutate({ tripId: loadTripId, body }, { onSuccess: onSaved });
      return;
    }
    if (reloadTripId !== null) {
      const body: ReloadBody = { tripId: reloadTripId, direction: 'ADD', lines, clientId };
      setAttempt({ kind: 'reload', body });
      reload.mutate(body, { onSuccess: onSaved });
    }
  }

  /** Belirsiz hata: aynı gövde ve aynı clientId ile tekrar atılır (idempotency). */
  function retry(): void {
    if (!attempt) return;
    if (attempt.kind === 'load') {
      tripLoad.mutate({ tripId: attempt.tripId, body: attempt.body }, { onSuccess: onSaved });
      return;
    }
    reload.mutate(attempt.body, { onSuccess: onSaved });
  }

  return (
    <section
      className="card"
      style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>
          {loadTripId !== null ? t('panel.depot.loadTitle') : t('panel.depot.reloadOnRoadTitle')}
        </strong>
        <span className="badge-neutral">{PLAN_SCOPE_LABEL[v.planScope]}</span>
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
        {t('panel.depot.loadOnVehicleNote')}
      </p>
      {v.capped ? (
        <p style={{ fontSize: 12.5, color: 'var(--warning)', fontWeight: 600 }}>
          {t('panel.depot.loadCapped')}
        </p>
      ) : null}
      {saved ? (
        <strong style={{ fontSize: 13.5, color: 'var(--primary)' }}>
          {loadTripId !== null ? t('panel.depot.loadSaved') : t('panel.depot.reloadSaved')}
        </strong>
      ) : null}

      {v.loadPlan.length === 0 ? (
        <p style={{ fontSize: 13 }}>{t('panel.depot.loadNothing')}</p>
      ) : (
        <>
          <strong style={{ fontSize: 13 }}>{t('panel.depot.loadSuggested')}</strong>
          {v.loadPlan.map((line) => (
            <div key={line.productId} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ flex: 1, fontWeight: 500 }}>{line.name}</span>
              <input
                aria-label={`${line.name} ${t('stock.qty')}`}
                inputMode="numeric"
                value={values[line.productId] ?? ''}
                onChange={(e) => setQty(line.productId, e.target.value)}
                style={{ width: 120, height: 46 }}
              />
            </div>
          ))}
        </>
      )}

      {invalid ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>{t('panel.depot.invalidQty')}</p>
      ) : null}
      {error ? <ErrorLine error={error} /> : null}
      {error && isAmbiguous(error) && attempt !== null ? (
        <div>
          <button type="button" className="btn" onClick={retry} disabled={busy}>
            {t('panel.depot.loadRetry')}
          </button>
        </div>
      ) : null}
      {v.loadPlan.length > 0 ? (
        <div>
          <button type="button" className="btn btn-primary" onClick={save} disabled={!canSave}>
            {loadTripId !== null ? t('common.save') : t('panel.depot.reloadOnRoadSave')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

type QtyPair = { full: string; empty: string };

/** Ham giriş → tam sayı; boş ya da geçersiz `null`. Parse dışında hiçbir hesap yok. */
function parseQty(raw: string): number | null {
  const s = raw.trim();
  return /^\d+$/.test(s) ? Number(s) : null;
}

/** Her ürün için FULL; damacanada ayrıca EMPTY. CASE_05L yalnız FULL (kural D3). Eksikse null. */
function buildCountLines(
  items: Product[],
  values: Record<string, QtyPair>,
): DepotCountLine[] | null {
  const lines: DepotCountLine[] = [];
  for (const p of items) {
    const pair = values[p.id] ?? { full: '', empty: '' };
    const full = parseQty(pair.full);
    if (full === null) return null;
    lines.push({ kind: p.kind, productId: p.id, condition: 'FULL', qty: full });
    if (p.kind === 'CASE_05L') continue;
    const empty = parseQty(pair.empty);
    if (empty === null) return null;
    lines.push({ kind: p.kind, productId: p.id, condition: 'EMPTY', qty: empty });
  }
  return lines;
}

/** Kör akşam sayımı: beklenen adet GÖSTERİLMEZ. Kayıttan sonra yalnız fark satırları çıkar. */
function CountForm({ onDone }: { onDone: () => void }) {
  const products = useProducts();
  const create = useCreateDepotCount();
  const [clientId] = useState(() => crypto.randomUUID());
  const [values, setValues] = useState<Record<string, QtyPair>>({});
  const [countId, setCountId] = useState<string | null>(null);
  const items = (products.data?.items ?? []).filter((p) => p.isActive);
  const lines = buildCountLines(items, values);
  const canSave = lines !== null && lines.length > 0 && !create.isPending;

  if (countId !== null) {
    return <CountResult id={countId} products={items} onDone={onDone} />;
  }

  function setQty(productId: string, field: keyof QtyPair, value: string): void {
    setValues((prev) => {
      const pair = prev[productId] ?? { full: '', empty: '' };
      return { ...prev, [productId]: { ...pair, [field]: value } };
    });
  }

  function save(): void {
    if (!lines || lines.length === 0) return;
    create.mutate(
      {
        clientId,
        scope: 'DEPOT',
        countedAt: new Date().toISOString(),
        lines,
      },
      { onSuccess: (res) => setCountId(res.id) },
    );
  }

  return (
    <section
      className="card"
      style={{
        padding: 20,
        borderColor: 'var(--primary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <strong style={{ fontSize: 15 }}>{t('panel.depot.countTitle')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('panel.depot.countHint')}</p>

      {!products.isPending && items.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>{t('panel.depot.noActiveProducts')}</p>
      ) : null}
      <div style={{ display: 'flex', gap: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
        <span style={{ flex: 1 }}>{t('stock.product')}</span>
        <span style={{ width: 120 }}>{t('stock.full')}</span>
        <span style={{ width: 120 }}>{t('stock.empty')}</span>
      </div>
      {items.map((p) => {
        const pair = values[p.id] ?? { full: '', empty: '' };
        return (
          <div key={p.id} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ flex: 1, fontWeight: 500 }}>{p.name}</span>
            <input
              aria-label={`${p.name} ${t('stock.full')}`}
              inputMode="numeric"
              value={pair.full}
              onChange={(e) => setQty(p.id, 'full', e.target.value)}
              style={{ width: 120, height: 46 }}
            />
            {p.kind === 'CASE_05L' ? (
              <span style={{ width: 120, color: 'var(--text-secondary)' }}>—</span>
            ) : (
              <input
                aria-label={`${p.name} ${t('stock.empty')}`}
                inputMode="numeric"
                value={pair.empty}
                onChange={(e) => setQty(p.id, 'empty', e.target.value)}
                style={{ width: 120, height: 46 }}
              />
            )}
          </div>
        );
      })}

      {lines === null ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('panel.depot.countIncomplete')}
        </p>
      ) : null}
      {create.isError ? <ErrorLine error={create.error} /> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={onDone} disabled={create.isPending}>
          {t('common.cancel')}
        </button>
        <button className="btn btn-primary" onClick={save} disabled={!canSave}>
          {t('common.save')}
        </button>
      </div>
    </section>
  );
}

/** Kayıt sonrası sayımın penceresi: fark satırları, hareket listesi ve sunucu uyarısı. */
function CountResult({
  id,
  products,
  onDone,
}: {
  id: string;
  products: Product[];
  onDone: () => void;
}) {
  const win = useCountWindow(id);
  if (win.isError) return <ErrorLine error={win.error} />;
  if (!win.data) {
    return <p style={{ color: 'var(--text-secondary)' }}>{t('common.loading')}</p>;
  }
  const rows = win.data.delta.filter((r) => r.countDiff !== 0);
  const moves = win.data.context.moves;
  return (
    <section
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <strong style={{ fontSize: 15 }}>{t('panel.depot.countSaved')}</strong>
      <strong style={{ fontSize: 13 }}>{t('panel.depot.countDiffTitle')}</strong>
      {rows.length === 0 ? (
        <p style={{ fontSize: 13.5 }}>{t('panel.depot.noDiff')}</p>
      ) : (
        rows.map((r) => (
          <p key={`${r.productId}-${r.condition}`} style={{ fontSize: 13.5 }}>
            {t('panel.depot.diffRow', {
              product: products.find((p) => p.id === r.productId)?.name ?? '',
              condition: t(r.condition === 'FULL' ? 'stock.full' : 'stock.empty'),
              diff: r.countDiff,
            })}
          </p>
        ))
      )}
      {win.data.adjustmentWarning ? (
        <p style={{ fontSize: 13, color: 'var(--warning)' }}>{win.data.adjustmentWarning}</p>
      ) : null}
      {moves.length > 0 ? (
        <>
          <strong style={{ fontSize: 13 }}>{t('panel.depot.movesTitle')}</strong>
          {moves.map((m) => (
            <p key={m.id} style={{ fontSize: 13.5 }}>
              {t('panel.depot.moveRow', {
                plate: m.plate ?? '—',
                at: formatDateTime(m.occurredAt),
                qty: m.qty,
              })}
            </p>
          ))}
        </>
      ) : null}
      <div>
        <button className="btn btn-primary" onClick={onDone}>
          {t('common.ok')}
        </button>
      </div>
    </section>
  );
}

/** Adım 2: sabah ihtiyacı. Eksik, kapasite, ileri tarihli ve koli sayıları sunucudan gelir. */
function MorningStep({
  data,
  onReload,
}: {
  data: DepotDayResponse;
  onReload: (vehicleId: string) => void;
}) {
  return (
    <>
      <section
        className="card"
        style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <strong style={{ fontSize: 15 }}>{t('panel.depot.morningTitle')}</strong>
        {data.vehicles.map((v) => (
          <MorningRow key={v.vehicleId} vehicle={v} onReload={onReload} />
        ))}
      </section>
      <div>
        <Link to="/panel-atama" className="btn btn-primary">
          {t('panel.depot.toAssign')}
        </Link>
      </div>
    </>
  );
}

function MorningRow({
  vehicle: v,
  onReload,
}: {
  vehicle: DepotDayVehicle;
  onReload: (vehicleId: string) => void;
}) {
  const short = v.shortBy > 0;
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
        paddingTop: 10,
        borderTop: '1px solid var(--border)',
      }}
    >
      <span
        style={{
          flex: 1,
          fontSize: 13.5,
          fontWeight: 500,
          color: short ? 'var(--warning)' : undefined,
        }}
      >
        {short
          ? t('panel.depot.needShort', {
              plate: v.plate,
              full: v.stock.fullJugs,
              needed: v.neededJugs,
              short: v.shortBy,
            })
          : t('panel.depot.needOk', {
              plate: v.plate,
              full: v.stock.fullJugs,
              needed: v.neededJugs,
            })}
      </span>
      {v.neededCrates > 0 ? (
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('panel.depot.needCrates', { n: v.neededCrates })}
        </span>
      ) : null}
      {v.overCapacity ? <span className="badge-amber">{t('panel.depot.overCapacity')}</span> : null}
      {v.futureOrders > 0 ? (
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('panel.depot.futureOrders', { n: v.futureOrders })}
        </span>
      ) : null}
      {short ? (
        <button
          type="button"
          className="btn"
          style={{ fontSize: 13, padding: '8px 14px' }}
          onClick={() => onReload(v.vehicleId)}
        >
          {t('panel.depot.reloadAction')}
        </button>
      ) : null}
    </div>
  );
}

/** Adım 3: bekleyen ara yüklemeler (ortak kuyruk) ve patron ara yükleme formu. */
function ReloadStep({
  data,
  preselectedVehicleId,
}: {
  data: DepotDayResponse;
  preselectedVehicleId: string | null;
}) {
  const pending = useReloadRequests('PENDING');
  const [open, setOpen] = useState(false);
  const items = pending.data?.items ?? [];
  return (
    <>
      {pending.isPending ? (
        <p style={{ color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
      ) : items.length > 0 ? (
        <ReloadQueue items={items} />
      ) : (
        <section className="card" style={{ padding: 18 }}>
          <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
            {t('panel.depot.queueEmpty')}
          </p>
        </section>
      )}

      <div>
        <button className="btn btn-primary" disabled={open} onClick={() => setOpen(true)}>
          {t('panel.depot.reloadOpen')}
        </button>
      </div>
      {open ? (
        <ReloadForm
          vehicles={data.vehicles}
          preselectedVehicleId={preselectedVehicleId}
          onDone={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Ara yükleme: yalnız sunucunun `reloadableTripId` verdiği araç seçilebilir (LOADING/ON_ROUTE).
 * Seçili araç yüklenemezse sessizce başka araca geçilmez; uyarı çıkar ve kayıt kapalı kalır.
 */
function ReloadForm({
  vehicles,
  preselectedVehicleId,
  onDone,
}: {
  vehicles: DepotDayVehicle[];
  preselectedVehicleId: string | null;
  onDone: () => void;
}) {
  const products = useProducts();
  const create = useCreateReload();
  const [clientId] = useState(() => crypto.randomUUID());
  const openVehicles = vehicles.filter((v) => v.openTripId !== null);
  const [vehicleId, setVehicleId] = useState(preselectedVehicleId ?? '');
  const [direction, setDirection] = useState<'ADD' | 'REMOVE'>('ADD');
  const [qty, setQty] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const jugs = (products.data?.items ?? []).filter(
    (p) => p.isActive && (RETURNABLE_JUG_KINDS as readonly string[]).includes(p.kind),
  );
  // Seçim tutarlı: listede olmayan ya da boş kimlikte ilk ara yüklenebilir araç.
  const selected =
    openVehicles.find((v) => v.vehicleId === vehicleId) ??
    openVehicles.find((v) => v.reloadableTripId !== null);
  const tripId = selected?.reloadableTripId ?? null;
  const notReloadable = selected !== undefined && tripId === null;

  const parsed = jugs.map((p) => {
    const raw = (qty[p.id] ?? '').trim();
    return { productId: p.id, qty: raw === '' ? 0 : parseQty(raw) };
  });
  const invalid = parsed.some((l) => l.qty === null);
  const lines = parsed.flatMap((l) =>
    l.qty !== null && l.qty > 0 ? [{ productId: l.productId, qty: l.qty }] : [],
  );
  const canSave = tripId !== null && !invalid && lines.length > 0 && !create.isPending;

  function save(): void {
    if (tripId === null || !canSave) return;
    create.mutate({ tripId, direction, lines, clientId }, { onSuccess: () => setSaved(true) });
  }

  if (saved) {
    return (
      <section
        className="card"
        style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        <strong style={{ fontSize: 15 }}>{t('panel.depot.reloadSaved')}</strong>
        <div>
          <button className="btn btn-primary" onClick={onDone}>
            {t('common.ok')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="card"
      style={{
        padding: 20,
        borderColor: 'var(--primary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <strong style={{ fontSize: 15 }}>{t('panel.depot.reloadTitle')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('panel.depot.reloadHint')}</p>

      {openVehicles.length === 0 ? (
        <p style={{ fontSize: 13.5 }}>{t('panel.depot.reloadNoTrip')}</p>
      ) : (
        <div
          className="form-grid"
          style={{ '--form-cols': '220px 220px', alignItems: 'end' } as CSSProperties}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            {t('panel.depot.reloadVehicle')}
            <select
              value={selected?.vehicleId ?? ''}
              onChange={(e) => setVehicleId(e.target.value)}
              style={{ height: 46 }}
            >
              {openVehicles.map((v) => (
                <option
                  key={v.vehicleId}
                  value={v.vehicleId}
                  disabled={v.reloadableTripId === null}
                >
                  {v.reloadableTripId === null
                    ? `${v.plate} · ${t('panel.depot.tripNotReloadable')}`
                    : v.plate}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            {t('panel.depot.reloadDirection')}
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value as 'ADD' | 'REMOVE')}
              style={{ height: 46 }}
            >
              <option value="ADD">{t('panel.depot.reloadAdd')}</option>
              <option value="REMOVE">{t('panel.depot.reloadRemove')}</option>
            </select>
          </label>
        </div>
      )}
      {notReloadable ? (
        <p style={{ fontSize: 13, color: 'var(--warning)', fontWeight: 600 }}>
          {t('panel.depot.vehicleNotReloadable')}
        </p>
      ) : null}

      {!products.isPending && jugs.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>{t('panel.depot.noActiveProducts')}</p>
      ) : null}
      {jugs.map((p) => (
        <div key={p.id} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ flex: 1, fontWeight: 500 }}>{p.name}</span>
          <input
            aria-label={`${p.name} ${t('stock.qty')}`}
            inputMode="numeric"
            value={qty[p.id] ?? ''}
            onChange={(e) => setQty((prev) => ({ ...prev, [p.id]: e.target.value }))}
            style={{ width: 120, height: 46 }}
          />
        </div>
      ))}

      {invalid ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>{t('panel.depot.invalidQty')}</p>
      ) : null}
      {!invalid && lines.length === 0 && tripId !== null ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('panel.depot.reloadNoLines')}
        </p>
      ) : null}
      {create.isError ? <ErrorLine error={create.error} /> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={onDone} disabled={create.isPending}>
          {t('common.cancel')}
        </button>
        <button className="btn btn-primary" onClick={save} disabled={!canSave}>
          {t('common.save')}
        </button>
      </div>
    </section>
  );
}

/** Adım 4: sevkiyat teslim alma bilgi kartı ve formu. */
function ShipmentStep() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <section
        className="card"
        style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        <strong style={{ fontSize: 15 }}>{t('panel.depot.shipmentTitle')}</strong>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('panel.depot.shipmentHint')}
        </p>
      </section>
      <div>
        <button className="btn btn-primary" disabled={open} onClick={() => setOpen(true)}>
          {t('panel.depot.shipmentOpen')}
        </button>
      </div>
      {open ? <ShipmentReceiveForm onDone={() => setOpen(false)} /> : null}
    </>
  );
}

type ForeignRow = { brandName: string; qty: string };

/** Boş alan `undefined` (isteğe bağlı alan gönderilmez); geçersiz giriş `null`. */
function optionalQty(raw: string): number | undefined | null {
  return raw.trim() === '' ? undefined : parseQty(raw);
}

/** Depo açısından işaretli fark: pozitifse "+" (yalnız biçim; hesap yok). */
function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}

/**
 * Sevkiyat teslim alma. İrsaliye no zorunlu; boşken istek hiç atılmaz. Fark satırını sunucu
 * yazar (kural 1); panel yalnız sonucu gösterir.
 */
function ShipmentReceiveForm({ onDone }: { onDone: () => void }) {
  const products = useProducts();
  const receive = useReceiveShipment();
  const [clientId] = useState(() => crypto.randomUUID());
  const jugs = (products.data?.items ?? []).filter(
    (p) => p.brandId !== null && (RETURNABLE_JUG_KINDS as readonly string[]).includes(p.kind),
  );
  const [productId, setProductId] = useState('');
  const [waybillNo, setWaybillNo] = useState('');
  const [qtyFull, setQtyFull] = useState('');
  const [qtyEmptyOut, setQtyEmptyOut] = useState('');
  const [countedFull, setCountedFull] = useState('');
  const [countedEmptyOut, setCountedEmptyOut] = useState('');
  const [foreign, setForeign] = useState<ForeignRow[]>([]);
  const selected = jugs.find((p) => p.id === (productId || jugs[0]?.id));

  const full = parseQty(qtyFull);
  const empty = parseQty(qtyEmptyOut);
  const cFull = optionalQty(countedFull);
  const cEmpty = optionalQty(countedEmptyOut);
  const foreignParsed = foreign.map((r) => ({
    brandName: r.brandName.trim(),
    qty: parseQty(r.qty),
  }));
  // Sunucu yabancı boş adedini en az 1 ister; 0 ya da boş satır gönderilmez, form kapalı kalır.
  const foreignValid = foreignParsed.every(
    (r) => r.brandName !== '' && r.qty !== null && r.qty >= 1,
  );
  const numbersValid = full !== null && empty !== null && cFull !== null && cEmpty !== null;
  const canSave =
    selected !== undefined &&
    waybillNo.trim() !== '' &&
    numbersValid &&
    foreignValid &&
    !receive.isPending;
  const touched =
    [qtyFull, qtyEmptyOut, countedFull, countedEmptyOut].some((s) => s.trim() !== '') ||
    foreign.length > 0;

  function save(): void {
    if (!selected?.brandId || !canSave || full === null || empty === null) return;
    if (cFull === null || cEmpty === null) return;
    const foreignBody = foreignParsed.flatMap((r) =>
      r.qty !== null && r.qty >= 1 ? [{ brandName: r.brandName, qty: r.qty }] : [],
    );
    receive.mutate({
      brandId: selected.brandId,
      productId: selected.id,
      waybillNo: waybillNo.trim(),
      qtyFull: full,
      qtyEmptyOut: empty,
      ...(cFull !== undefined ? { countedFull: cFull } : {}),
      ...(cEmpty !== undefined ? { countedEmptyOut: cEmpty } : {}),
      ...(foreignBody.length > 0 ? { foreign: foreignBody } : {}),
      clientId,
    });
  }

  const result = receive.data;
  if (result) {
    return (
      <section
        className="card"
        style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        <strong style={{ fontSize: 15 }}>{t('panel.depot.received')}</strong>
        {result.adjustments.length === 0 ? (
          <p style={{ fontSize: 13.5 }}>{t('panel.depot.noAdjustment')}</p>
        ) : (
          result.adjustments.map((a) => (
            <p key={a.condition} style={{ fontSize: 13.5 }}>
              {t('panel.depot.adjustmentLine', {
                condition: t(a.condition === 'FULL' ? 'stock.full' : 'stock.empty'),
                qty: signed(a.qty),
              })}
            </p>
          ))
        )}
        {result.foreignReturned > 0 ? (
          <p style={{ fontSize: 13.5 }}>
            {t('panel.depot.foreignReturned', { n: result.foreignReturned })}
          </p>
        ) : null}
        <div>
          <button className="btn btn-primary" onClick={onDone}>
            {t('common.ok')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="card"
      style={{
        padding: 20,
        borderColor: 'var(--primary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <strong style={{ fontSize: 15 }}>{t('panel.depot.shipmentTitle')}</strong>
      {!products.isPending && jugs.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>{t('panel.depot.noActiveProducts')}</p>
      ) : null}
      <div
        className="form-grid"
        style={{ '--form-cols': '220px 1fr 1fr', alignItems: 'end' } as CSSProperties}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stock.product')}
          <select
            value={selected?.id ?? ''}
            onChange={(e) => setProductId(e.target.value)}
            style={{ height: 46 }}
          >
            {jugs.map((p) => (
              <option key={p.id} value={p.id}>
                {t('prod.nameWithKind', { name: p.name, kind: t(`productKind.${p.kind}`) })}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stock.waybill')}
          <input
            value={waybillNo}
            onChange={(e) => setWaybillNo(e.target.value)}
            style={{ height: 46 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('panel.depot.waybillFull')}
          <input
            inputMode="numeric"
            value={qtyFull}
            onChange={(e) => setQtyFull(e.target.value)}
            style={{ height: 46 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('panel.depot.waybillEmptyOut')}
          <input
            inputMode="numeric"
            value={qtyEmptyOut}
            onChange={(e) => setQtyEmptyOut(e.target.value)}
            style={{ height: 46 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('panel.depot.countedFull')}
          <input
            inputMode="numeric"
            value={countedFull}
            onChange={(e) => setCountedFull(e.target.value)}
            style={{ height: 46 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('panel.depot.countedEmptyOut')}
          <input
            inputMode="numeric"
            value={countedEmptyOut}
            onChange={(e) => setCountedEmptyOut(e.target.value)}
            style={{ height: 46 }}
          />
        </label>
      </div>

      <strong style={{ fontSize: 13.5 }}>{t('panel.depot.foreignTitle')}</strong>
      {foreign.map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            aria-label={t('panel.depot.foreignBrand')}
            placeholder={t('panel.depot.foreignBrand')}
            value={row.brandName}
            onChange={(e) =>
              setForeign((rows) =>
                rows.map((r, j) => (j === i ? { ...r, brandName: e.target.value } : r)),
              )
            }
            style={{ flex: 1, height: 46 }}
          />
          <input
            aria-label={t('stock.qty')}
            inputMode="numeric"
            value={row.qty}
            onChange={(e) =>
              setForeign((rows) =>
                rows.map((r, j) => (j === i ? { ...r, qty: e.target.value } : r)),
              )
            }
            style={{ width: 120, height: 46 }}
          />
          <button
            type="button"
            className="btn"
            onClick={() => setForeign((rows) => rows.filter((_, j) => j !== i))}
          >
            {t('common.delete')}
          </button>
        </div>
      ))}
      {foreign.length < FOREIGN_MAX ? (
        <div>
          <button
            type="button"
            className="btn"
            onClick={() => setForeign((rows) => [...rows, { brandName: '', qty: '' }])}
          >
            {t('panel.depot.foreignAdd')}
          </button>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('panel.depot.foreignMax')}
        </p>
      )}

      {waybillNo.trim() === '' ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('panel.depot.waybillRequired')}
        </p>
      ) : null}
      {touched && (!numbersValid || !foreignValid) ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>{t('panel.depot.invalidQty')}</p>
      ) : null}
      {receive.isError ? <ErrorLine error={receive.error} /> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={onDone} disabled={receive.isPending}>
          {t('common.cancel')}
        </button>
        <button className="btn btn-primary" onClick={save} disabled={!canSave}>
          {t('panel.depot.shipmentSave')}
        </button>
      </div>
    </section>
  );
}

/** Mutasyon hatasını kullanıcıya gösterir; hata SESSİZ kalmaz (sunucu mesajı öncelikli). */
function ErrorLine({ error }: { error: unknown }) {
  const message = (error as { message?: string } | null)?.message;
  return (
    <p style={{ fontSize: 13, color: 'var(--danger)' }}>
      {message && message.length > 0 ? message : t('error.internal')}
    </p>
  );
}
